import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Icon,
  Text,
  useColorModeValue,
  Tag,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  Tooltip,
  Progress,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FiAlertTriangle, FiTrash2, FiSearch, FiChevronDown, FiChevronRight, FiExternalLink } from "react-icons/fi";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import {
  getAllCustomerOrders,
  deleteOrder
} from "../../../../services/woo";

const MONTHS = [
  ['01', 'Enero'], ['02', 'Febrero'], ['03', 'Marzo'], ['04', 'Abril'],
  ['05', 'Mayo'], ['06', 'Junio'], ['07', 'Julio'], ['08', 'Agosto'],
  ['09', 'Septiembre'], ['10', 'Octubre'], ['11', 'Noviembre'], ['12', 'Diciembre']
];

const ORDER_STATUSES = [
  ['processing', 'Procesando'],
  ['completed', 'Pagada / completada'],
  ['on-hold', 'En espera'],
  ['pending', 'Pendiente'],
  ['failed', 'Fallida'],
  ['refunded', 'Reembolsada'],
  ['cancelled', 'Cancelada'],
  ['trash', 'Papelera']
];

const PROTECTED_STATUSES = ['processing', 'completed', 'refunded'];

/**
 * Decide qué órdenes de un grupo duplicado se conservan, cuáles se pueden
 * eliminar y cuáles requieren revisión manual.
 *
 * Regla: si existe al menos una orden protegida (pagada/procesada/reembolsada),
 * esa es la que cuenta como cobro válido del mes, así que TODAS las órdenes
 * no protegidas del grupo son excedentes y se pueden eliminar, sin importar
 * cuál se creó primero. Solo cuando no hay ninguna orden protegida se usa la
 * regla de "conservar la más antigua, eliminar el resto" (para dejarle al
 * alumno una orden pendiente que pueda pagar).
 *
 * Si hay MÁS DE UNA orden protegida para el mismo mes+concepto, eso significa
 * que ya se cobró dos veces y se pagó dos veces: no se puede auto-eliminar
 * (nunca se tocan órdenes pagadas), pero se marca needsManualReview y se sigue
 * cuantificando el excedente en el monto para que no quede oculto en $0.
 */
function computeGroupPlan(orders) {
  const protectedOrders = orders.filter(o => o.protected);
  const unprotectedOrders = orders.filter(o => !o.protected);
  const hasProtected = protectedOrders.length > 0;

  let toDelete = [];
  let toKeep = [];
  let needsManualReview = false;
  let excessAmount = 0;

  if (hasProtected) {
    toDelete = unprotectedOrders;
    toKeep = protectedOrders;

    if (protectedOrders.length > 1) {
      needsManualReview = true;
      const sortedByTotal = [...protectedOrders].sort((a, b) => parseFloat(b.total || 0) - parseFloat(a.total || 0));
      excessAmount += sortedByTotal.slice(1).reduce((sum, o) => sum + parseFloat(o.total || 0), 0);
    }

    excessAmount += toDelete.reduce((sum, o) => sum + parseFloat(o.total || 0), 0);
  } else {
    const sortedByDate = [...unprotectedOrders].sort((a, b) => new Date(a.date_created) - new Date(b.date_created));
    toKeep = sortedByDate.slice(0, 1);
    toDelete = sortedByDate.slice(1);
    excessAmount = toDelete.reduce((sum, o) => sum + parseFloat(o.total || 0), 0);
  }

  return {
    toDelete,
    toKeep,
    needsManualReview,
    excessAmount,
    deleteIds: new Set(toDelete.map(o => o.order_id))
  };
}

function BatchUsersOrderDuplicateFinder({ title, customers, onDuplicatesCleaned }) {
  const titleColor = useColorModeValue("teal.300", "teal.200");
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("white", "gray.700");

  const toast = useToast();

  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [totalProcessed, setTotalProcessed] = useState(0);
  const [loadingScan, setLoadingScan] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  const [asyncProgress, setAsyncProgress] = useState({ progress: 0, total: 0 });

  /**
   * Escanear órdenes duplicadas con una llamada secuencial por alumno.
   */
  const scanDuplicates = async () => {
    setLoadingScan(true);
    setDuplicateGroups([]);
    setExpandedGroups({});
    setAsyncProgress({ progress: 0, total: 0 });

    try {
      const groups = {};
      let errorCount = 0;

      // Igual que Auditar Cobros: una llamada por alumno, en serie.
      for (let index = 0; index < customers.length; index++) {
        const customer = customers[index];
        setAsyncProgress({ progress: index, total: customers.length });

        try {
          const orders = await getAllCustomerOrders(customer.id);

          orders
            .filter(order => {
              const subscriptionMonth = order.meta_data?.find(meta => meta.key === 'mes')?.value || '';
              const [subscriptionYear, subscriptionMonthNumber] = subscriptionMonth.split('-');
              const statusMatches = selectedStatus === 'all'
                ? order.status !== 'trash' && order.status !== 'cancelled'
                : order.status === selectedStatus;

              return statusMatches
                && (!selectedMonth || subscriptionMonthNumber === selectedMonth)
                && (!selectedYear || subscriptionYear === selectedYear);
            })
            .forEach(order => {
              const month = order.meta_data?.find(meta => meta.key === 'mes')?.value;
              if (!month) return;

              const lineItem = order.line_items?.[0];
              const concept = lineItem?.meta_data?.find(meta => meta.key === 'concepto')?.value
                || order.meta_data?.find(meta => meta.key === 'concepto')?.value
                || 'Sin concepto';

              const key = `${customer.id}|${month}|${concept}`;
              if (!groups[key]) {
                groups[key] = {
                  user: {
                    id: customer.id,
                    name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.name || `#${customer.id}`,
                    email: customer.email || ''
                  },
                  mes: month,
                  concepto: concept,
                  orders: []
                };
              }

              groups[key].orders.push({
                order_id: order.id,
                status: order.status,
                total: order.total,
                date_created: order.date_created,
                date_paid: order.date_paid,
                protected: PROTECTED_STATUSES.includes(order.status) || Boolean(order.date_paid)
              });
            });
        } catch (error) {
          errorCount++;
          console.error(`Error escaneando órdenes del cliente ${customer.id}:`, error);
        }
      }

      const duplicates = Object.values(groups)
        .filter(group => group.orders.length > 1)
        .slice(0, 500)
        .map(group => ({
          ...group,
          duplicate_count: group.orders.length,
          total_amount: group.orders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0),
          plan: computeGroupPlan(group.orders)
        }));

      setDuplicateGroups(duplicates);
      setTotalProcessed(customers.length);
      setAsyncProgress({ progress: customers.length, total: customers.length });

      toast({
        title: duplicates.length > 0
          ? `${duplicates.length} grupo(s) con duplicados encontrados`
          : 'No se encontraron órdenes duplicadas',
        description: errorCount > 0 ? `${errorCount} alumno(s) no pudieron consultarse` : undefined,
        status: duplicates.length > 0 ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Error scanning duplicates:', err);
      toast({
        title: 'Error al escanear',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    setLoadingScan(false);
  };

  /**
   * Eliminar órdenes duplicadas (mantiene la más antigua, elimina el resto)
   */
  const cleanDuplicates = async () => {
    setLoadingDelete(true);

    // Nunca eliminar órdenes pagadas/procesadas/reembolsadas.
    // Si el grupo ya tiene una orden protegida, todas las no protegidas
    // son excedente (sin importar cuál se creó primero). Si no hay ninguna
    // protegida, se conserva la más antigua y se elimina el resto.
    const ordersToDelete = [];
    duplicateGroups.forEach(group => {
      group.plan.toDelete.forEach(o => ordersToDelete.push(o));
    });

    if (ordersToDelete.length === 0) {
      setLoadingDelete(false);
      return;
    }

    let deletedCount = 0;
    let errorCount = 0;

    for (const order of ordersToDelete) {
      try {
        const result = await deleteOrder(order.order_id);
        if (result) {
          deletedCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        console.error(`Error eliminando orden ${order.order_id}:`, err);
        errorCount++;
      }
    }

    toast({
      title: 'Limpieza completada',
      description: `${deletedCount} orden(es) eliminada(s)${errorCount > 0 ? `, ${errorCount} error(es)` : ''}`,
      status: errorCount > 0 ? 'warning' : 'success',
      duration: 5000,
      isClosable: true,
    });

    // Refrescar lista para ver el resultado
    if (deletedCount > 0) {
      await scanDuplicates();
      if (onDuplicatesCleaned) onDuplicatesCleaned();
    }

    setLoadingDelete(false);
  };

  /**
   * Toggle expandir/colapsar un grupo de duplicados
   */
  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Total de órdenes duplicadas a eliminar
  const totalExcessOrders = duplicateGroups.reduce((acc, group) => acc + group.plan.toDelete.length, 0);
  const totalExcessAmount = duplicateGroups.reduce((acc, group) => acc + group.plan.excessAmount, 0);
  const groupsNeedingReview = duplicateGroups.filter(group => group.plan.needsManualReview).length;

  return (
    <Card overflowX={{ sm: "scroll", xl: "hidden" }} border={{ xl: "1px" }} mb="6">
      <CardHeader p="6px 0px 22px 0px">
        <Text
          fontSize="xl"
          color={textColor}
          fontWeight="bold"
          textAlign="center"
          mb="22px"
        >
          {title || 'Buscar Órdenes Duplicadas'}
        </Text>
      </CardHeader>
      <CardBody>
        <Box w="100%">
          {/* Resumen de clientes */}
          <Flex align="center" gap={3} mb={4} flexWrap="wrap">
            <Badge fontSize="0.8em" colorScheme="teal">
              {customers.length} usuarios seleccionados
            </Badge>
            {duplicateGroups.length > 0 && (
              <Badge fontSize="0.8em" colorScheme="orange">
                {duplicateGroups.length} grupo(s) duplicado(s)
              </Badge>
            )}
            {totalExcessOrders > 0 && (
              <Badge fontSize="0.8em" colorScheme="red">
                {totalExcessOrders} orden(es) excesiva(s)
              </Badge>
            )}
            {groupsNeedingReview > 0 && (
              <Badge fontSize="0.8em" colorScheme="purple">
                {groupsNeedingReview} grupo(s) pagado(s) dos veces — revisión manual
              </Badge>
            )}
          </Flex>

          {/* Controles */}
          <FormControl>
            <FormLabel fontSize="sm" color={textColor}>Filtros de mensualidad</FormLabel>
            <Flex gap={3} mb="24px" flexWrap="wrap">
              <Select
                size="lg"
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                placeholder="Mes"
                flex="1"
                minW="130px"
              >
                {MONTHS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Select>
              <Select
                size="lg"
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value)}
                placeholder="Año"
                flex="1"
                minW="110px"
              >
                {[currentYear - 1, currentYear, currentYear + 1].map(year => (
                  <option key={year} value={String(year)}>{year}</option>
                ))}
              </Select>
              <Select
                size="lg"
                value={selectedStatus}
                onChange={e => setSelectedStatus(e.target.value)}
                flex="1"
                minW="170px"
              >
                <option value="all">Estados activos</option>
                {ORDER_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Select>
            </Flex>
            <Text fontSize="xs" color="gray.500" mb={3}>
              El mes y año corresponden a la mensualidad guardada en la orden, no a su fecha de creación.
              Las órdenes pagadas, procesadas o reembolsadas nunca se eliminarán.
            </Text>

            <Button
              bg="orange.400"
              fontSize="14px"
              color="white"
              fontWeight="bold"
              w="100%"
              h="45"
              mb="12px"
              leftIcon={<Icon as={FiSearch} />}
              _hover={{ bg: "orange.300" }}
              _active={{ bg: "orange.500" }}
              onClick={scanDuplicates}
              isLoading={loadingScan}
              loadingText="Buscando..."
            >
              BUSCAR DUPLICADOS
            </Button>

            {totalExcessOrders > 0 && (
              <Button
                bg="red.500"
                fontSize="14px"
                color="white"
                fontWeight="bold"
                w="100%"
                h="45"
                mb="24px"
                leftIcon={<Icon as={FiTrash2} />}
                _hover={{ bg: "red.400" }}
                _active={{ bg: "red.600" }}
                onClick={cleanDuplicates}
                isLoading={loadingDelete}
                loadingText="Eliminando..."
              >
                ELIMINAR {totalExcessOrders} DUPLICADO(S) NO PAGADO(S)
              </Button>
            )}
          </FormControl>

          {/* Resultados */}
          {loadingScan && (
            <Box py={6}>
              <Flex align="center" justify="center" mb={3} gap={3}>
                <Spinner color="orange.400" />
                <Text color={textColor}>Escaneando órdenes... {asyncProgress.progress}/{asyncProgress.total} clientes</Text>
              </Flex>
              <Progress
                value={asyncProgress.total > 0 ? (asyncProgress.progress / asyncProgress.total) * 100 : 0}
                size="sm"
                colorScheme="orange"
                borderRadius="md"
                hasStripe
                isAnimated
              />
            </Box>
          )}

          {!loadingScan && duplicateGroups.length === 0 && totalProcessed > 0 && (
            <Flex align="center" justify="center" py={8}>
              <Text color="green.500" fontWeight="bold">
                ✅ No se encontraron órdenes duplicadas
              </Text>
            </Flex>
          )}

          {/* Tabla de duplicados */}
          {duplicateGroups.length > 0 && (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th></Th>
                    <Th>Usuario</Th>
                    <Th>Mes</Th>
                    <Th>Concepto</Th>
                    <Th isNumeric>Duplicados</Th>
                    <Th isNumeric>Monto excedente</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {duplicateGroups.map((group, idx) => {
                    const groupKey = `${group.user.id}-${group.mes}-${group.concepto}-${idx}`;
                    const isExpanded = expandedGroups[groupKey];
                    const sortedOrders = [...group.orders].sort((a, b) =>
                      new Date(a.date_created) - new Date(b.date_created)
                    );
                    const { excessAmount, needsManualReview, deleteIds } = group.plan;

                    return (
                      <React.Fragment key={groupKey}>
                        <Tr
                          _hover={{ bg: 'orange.50' }}
                          cursor="pointer"
                          onClick={() => toggleGroup(groupKey)}
                          bg={isExpanded ? 'orange.50' : 'transparent'}
                        >
                          <Td>
                            <Icon as={isExpanded ? FiChevronDown : FiChevronRight} />
                          </Td>
                          <Td>
                            <Flex align="center" gap={2}>
                              <Tooltip label={group.user.email || ''}>
                                <Text fontSize="sm" fontWeight="500" noOfLines={1}>
                                  {group.user.name || `#${group.user.id}`}
                                </Text>
                              </Tooltip>
                              <Tooltip label="Ver perfil del alumno">
                                <a
                                  href={`${window.location.origin}/#/admin/usuario/${group.user.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={event => event.stopPropagation()}
                                >
                                  <Icon as={FiExternalLink} color="blue.500" />
                                </a>
                              </Tooltip>
                            </Flex>
                          </Td>
                          <Td>
                            <Tag size="sm" colorScheme="orange">{group.mes}</Tag>
                          </Td>
                          <Td>
                            <Text fontSize="sm" noOfLines={1}>{group.concepto}</Text>
                          </Td>
                          <Td isNumeric>
                            <Flex align="center" justify="flex-end" gap={1}>
                              <Badge colorScheme="red">{group.duplicate_count} órdenes</Badge>
                              {needsManualReview && (
                                <Tooltip label="Hay más de una orden pagada para el mismo mes y concepto. No se elimina automáticamente, requiere revisión manual.">
                                  <Badge colorScheme="purple">pagada 2x</Badge>
                                </Tooltip>
                              )}
                            </Flex>
                          </Td>
                          <Td isNumeric>
                            <Text fontSize="sm" color="red.500" fontWeight="bold">
                              ${excessAmount.toLocaleString('es-CL')}
                            </Text>
                          </Td>
                        </Tr>

                        {isExpanded && (
                          <Tr>
                            <Td colSpan={6} p={0}>
                              <Box pl={8} pr={4} py={2} bg="gray.50">
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                  {needsManualReview
                                    ? 'Hay más de una orden pagada/procesada para el mismo mes y concepto. Ninguna se elimina automáticamente — requiere revisión manual (posible reembolso o ajuste):'
                                    : 'Las órdenes pagadas/procesadas están protegidas. Solo se eliminarán órdenes no pagadas duplicadas:'}
                                </Text>
                                {sortedOrders.map((order, oi) => {
                                  const willDelete = deleteIds.has(order.order_id);
                                  return (
                                    <Flex
                                      key={oi}
                                      align="center"
                                      gap={3}
                                      py={1}
                                      px={2}
                                      borderRadius="md"
                                      bg={willDelete ? 'red.50' : 'green.50'}
                                      mb={1}
                                    >
                                      {order.protected ? (
                                        <Tag size="sm" colorScheme="green">PROTEGIDA</Tag>
                                      ) : willDelete ? (
                                        <Tag size="sm" colorScheme="red">ELIMINAR</Tag>
                                      ) : (
                                        <Tag size="sm" colorScheme="green">CONSERVAR</Tag>
                                      )}
                                      <Text fontSize="xs">
                                        Orden #{order.order_id} — {order.status} — ${parseFloat(order.total || 0).toLocaleString('es-CL')} — {order.date_created?.slice(0, 10)}
                                        {order.date_paid ? ' — pagada' : ''}
                                      </Text>
                                    </Flex>
                                  );
                                })}
                              </Box>
                            </Td>
                          </Tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Tbody>
              </Table>

              {/* Resumen final */}
              <Flex
                mt={4}
                p={3}
                bg="orange.50"
                borderRadius="md"
                align="center"
                justify="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <Text fontSize="sm" color="gray.600">
                  <Icon as={FiAlertTriangle} color="orange.500" mr={1} />
                  Se eliminarán <strong>{totalExcessOrders}</strong> orden(es) no pagada(s), sin tocar órdenes pagadas.
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="red.500">
                  Total excedente: ${totalExcessAmount.toLocaleString('es-CL')}
                </Text>
              </Flex>
            </Box>
          )}
        </Box>
      </CardBody>
    </Card>
  );
}

export default BatchUsersOrderDuplicateFinder;
